<script setup lang="ts">
import {
  EventSorting,
  EventStatus,
  ProductSorting,
  ProductStatus,
} from "mws-ts-v2-sdk";

import type { SearchItemClickedType } from "~/analytics/segment";
import { CategoryTrackingMapTypes, SearchResultTypes } from "~/types/Search";

type Props = {
  searchTerm: string;
  tab: SearchResultTypes;
};

const props = defineProps<Props>();
const tab = computed(() => props.tab);
const searchTerm = computed(() => props.searchTerm);

const { debounced: queryCategory } = useDebouncedRouteQuery("category");
const { debounced: queryPriceFrom } = useDebouncedRouteQuery("priceFrom");
const { debounced: queryPriceTo } = useDebouncedRouteQuery("priceTo");
const { debounced: querySort } = useDebouncedRouteQuery("sort");

const { debounced: queryEventStatus } =
  useDebouncedRouteQuery<EventStatus>("eventStatus");
const { debounced: queryProductStatus } =
  useDebouncedRouteQuery<ProductStatus>("productStatus");

const filters = computed(() => ({
  categoryIds: transformRouteParams(queryCategory.value, "array"),
  priceFrom: transformRouteParams(queryPriceFrom.value, "string"),
  priceTo: transformRouteParams(queryPriceTo.value, "string"),
  productStatus: transformRouteParams<ProductStatus>(
    queryProductStatus.value,
    "string",
  ),
  eventStatus: transformRouteParams<EventStatus>(
    queryEventStatus.value,
    "string",
  ),
}));

const sorting = computed(() =>
  transformRouteParams<ProductSorting | EventSorting>(
    querySort.value,
    "string",
  ),
);

const { searchItemClicked } = useAnalytics();
const onSearchItemClicked = (type: SearchItemClickedType, name: string) => {
  searchItemClicked({
    type,
    name,
    tab_origin: tab.value,
    search_term: searchTerm.value,
  });
};

const { activeCurrency } = useActiveCurrency();
const activeCurrencyIso = computed(() => activeCurrency.value.isoCurrencyCode);

const searchResults = useTabSearchResults({
  searchTerm,
  tab,
  filters,
  sorting,
  activeCurrency: activeCurrencyIso,
});
</script>

<template>
  <BaseLoadingSpinner
    v-if="searchResults.isLoading"
    logo-type="lightMode"
  />
  <template v-else>
    <LazyBasePlaceholderNoResults
      v-if="searchResults.count === 0 && tab !== SearchResultTypes.Products"
      class="m-12"
      variant="searchPage"
      data-cy="search-results-page-placeholder"
    />
    <template v-else>
      <div
        v-if="searchResults.type === SearchResultTypes.Products"
        class="block-container"
      >
        <ProductsOverviewHeader
          :are-products-loading="searchResults.isLoading"
          :product-count="searchResults.count"
        >
          <ProductFilter
            class="md:hidden ml-auto"
            type="search"
            :search-term="searchTerm"
            data-test="product-filter-button-mobile"
          />
        </ProductsOverviewHeader>

        <div class="site-viewport-container flex gap-6">
          <ProductFilterStickySidebar>
            <ProductFilter
              type="search"
              :search-term="searchTerm"
              :is-directly-visible="true"
            />
          </ProductFilterStickySidebar>
          <LazyBasePlaceholderNoResults
            v-if="searchResults.count === 0"
            class="min-h-screen m-12"
            variant="searchPage"
            data-cy="search-results-page-placeholder"
          />
          <ProductSectionProductsGrid
            v-else
            :products="searchResults.data"
            :virtualisation="searchResults.count > 25"
            tracking-source="searchPageProductsTab"
            class="site-viewport-container pb-4 pt-6 flex-grow"
            data-test="products-grid"
            @product-clicked="
              (product) => onSearchItemClicked('product', product.name)
            "
          />
        </div>
      </div>
      <SearchResultPageEvents
        v-else-if="searchResults.type === SearchResultTypes.Events"
        :event-results="searchResults.data"
        data-cy="search-results-page-event-results"
        @search-item-clicked="
          (event) => onSearchItemClicked('event', event.name)
        "
      />
      <SearchResultPageCategories
        v-else
        :category-results="searchResults.data"
        :category-type="searchResults.type"
        :hide-border="true"
        @search-item-clicked="
          (category, type) =>
            onSearchItemClicked(CategoryTrackingMapTypes[type], category.name)
        "
      />
      <BasePaginatorShowMore
        v-if="searchResults.hasNextPage"
        class="site-viewport-container"
        :amount-shown="searchResults.data.length"
        :total-count="searchResults.count"
        :enable-autoscroll="true"
        :is-fetching="searchResults.isFetching"
        @load-more="searchResults.fetchNextPage"
      />
    </template>
  </template>
</template>
